import { create } from 'zustand';
import { toast } from 'sonner';
import type { ExerciseDefinition } from '../types';
import { exercisesAPI } from '../lib/api';

interface CustomExercisesState {
  customExercises: ExerciseDefinition[];
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchExercises: () => Promise<void>;
  addExercise: (exercise: Omit<ExerciseDefinition, 'id'>) => Promise<ExerciseDefinition>;
  updateExercise: (id: string, updates: Partial<ExerciseDefinition>) => Promise<void>;
  deleteExercise: (id: string) => Promise<void>;
}

export const useCustomExercisesStore = create<CustomExercisesState>((set, get) => ({
  customExercises: [],
  loading: false,
  error: null,

  fetchExercises: async () => {
    set({ loading: true, error: null });
    try {
      const { exercises } = await exercisesAPI.list();
      
      // Map MongoDB documents to ExerciseDefinition format
      const mapped = exercises.map((ex: any) => ({
        id: ex._id,
        name: ex.name,
        movementPattern: ex.movementPattern,
        primaryMuscle: ex.primaryMuscle,
        secondaryMuscles: ex.secondaryMuscles,
        tag: ex.tag,
        defaultSets: ex.defaultSets,
        defaultReps: ex.defaultReps,
        defaultRPE: ex.defaultRPE,
        defaultRest: ex.defaultRest,
        defaultTempo: ex.defaultTempo,
      }));
      
      set({ customExercises: mapped, loading: false });
    } catch (error: any) {
      const message = error.message || 'Nie udało się wczytać ćwiczeń';
      set({ error: message, loading: false });
      toast.error(message);
      console.error('Failed to fetch exercises:', error);
    }
  },

  addExercise: async (data) => {
    set({ loading: true, error: null });
    try {
      const { exercise } = await exercisesAPI.create(data);
      
      const newExercise: ExerciseDefinition = {
        id: exercise._id,
        name: exercise.name,
        movementPattern: exercise.movementPattern,
        primaryMuscle: exercise.primaryMuscle,
        secondaryMuscles: exercise.secondaryMuscles,
        tag: exercise.tag,
        defaultSets: exercise.defaultSets,
        defaultReps: exercise.defaultReps,
        defaultRPE: exercise.defaultRPE,
        defaultRest: exercise.defaultRest,
        defaultTempo: exercise.defaultTempo,
      };
      
      set((s) => ({
        customExercises: [...s.customExercises, newExercise],
        loading: false,
      }));
      toast.success('Ćwiczenie dodane!');
      
      return newExercise;
    } catch (error: any) {
      const message = error.message || 'Nie udało się dodać ćwiczenia';
      set({ error: message, loading: false });
      toast.error(message);
      throw error;
    }
  },

  updateExercise: async (id, updates) => {
    set({ loading: true, error: null });
    try {
      const { exercise } = await exercisesAPI.update(id, updates);
      
      const updated: ExerciseDefinition = {
        id: exercise._id,
        name: exercise.name,
        movementPattern: exercise.movementPattern,
        primaryMuscle: exercise.primaryMuscle,
        secondaryMuscles: exercise.secondaryMuscles,
        tag: exercise.tag,
        defaultSets: exercise.defaultSets,
        defaultReps: exercise.defaultReps,
        defaultRPE: exercise.defaultRPE,
        defaultRest: exercise.defaultRest,
        defaultTempo: exercise.defaultTempo,
      };
      
      set((s) => ({
        customExercises: s.customExercises.map((e) =>
          e.id === id ? updated : e
        ),
        loading: false,
      }));
      toast.success('Ćwiczenie zaktualizowane!');
    } catch (error: any) {
      const message = error.message || 'Nie udało się zaktualizować ćwiczenia';
      set({ error: message, loading: false });
      toast.error(message);
      throw error;
    }
  },

  deleteExercise: async (id) => {
    set({ loading: true, error: null });
    try {
      await exercisesAPI.delete(id);
      
      set((s) => ({
        customExercises: s.customExercises.filter((e) => e.id !== id),
        loading: false,
      }));
      toast.success('Ćwiczenie usunięte');
    } catch (error: any) {
      const message = error.message || 'Nie udało się usunąć ćwiczenia';
      set({ error: message, loading: false });
      toast.error(message);
      throw error;
    }
  },
}));
